/**
 * WidgetInspector — the report editor's docked right-hand panel (TV1-2291 · S16).
 *
 * Replaces `WidgetBuilder`'s `Modal size="75%"`. The modal HID the report you were
 * configuring the widget against, so every measure change cost a
 * close-look-reopen cycle. This panel is part of the editor layout — canvas left,
 * inspector right, both live — so the card re-renders as you type.
 *
 * **Not a Drawer, not a Modal.** No overlay, no focus trap, no portal: the root is
 * a plain `<aside>` in the editor's flow. `.claude/rules/design-system.md` says
 * "complex forms: Modal + Stepper, NEVER Drawer"; that rule targets multi-step
 * CREATION flows, where a focus trap is a feature. This is an inspector for a
 * selected object, whose entire value is that the canvas stays visible. The
 * exception was taken on the ticket owner's call (2026-08-14) and is recorded in
 * the rule file by ticket 19.
 *
 * Two panes, one visible at a time:
 *
 * | Pane      | Contents                                              |
 * |-----------|-------------------------------------------------------|
 * | `layout`  | Report-level settings (name, description, facts)      |
 * | `card`    | The selected card: title, source config, tint, remove  |
 *
 * ⚠️ **THERE IS NO `palette` PANE, AND THE PANEL IS NOT ALWAYS MOUNTED.** Both are
 * deliberate reversals of how S16 shipped, made on the ticket owner's call
 * (2026-08-17) after using it: the panel was docked unconditionally next to a
 * FLOATING palette bar that also adds cards, so the editor showed two ways to add a
 * card and reserved 348px to say "nothing is selected".
 *
 *   ADDING  is the floating bar's job, and only its job (`WidgetToolbar`).
 *   EDITING is this panel's job, and it appears only when there is something to
 *           edit — a selected card, or report settings opened on purpose.
 *
 * Dropping the pane is what makes that true rather than merely stated: a `palette`
 * pane reachable from here would put the second entry point straight back.
 *
 * The seven `WidgetBuilder/Sections/*` components are mounted VERBATIM in the card
 * pane — same `commonProps` contract the modal fed them, only the host changes.
 * They are the reason this component is a host and not a rewrite.
 *
 * The panel is CONTROLLED: it owns no widget state. Every edit is emitted through
 * `onWidgetChange` on the keystroke — there is deliberately no Save button, since
 * a save round-trip is exactly the latency the modal imposed.
 */

import type { NormalizedCubeCatalog } from '../Widgets/WidgetBuilder/cubeMeta';
import { WIDGET_GAP_PX } from '../gridGeometry';
import {
  Accordion, ActionIcon, Badge, Button, ColorInput, Group, Slider, Stack, Text, TextInput, Textarea,
  UnstyledButton
} from '@mantine/core';
import { IconArrowLeft, IconRestore, IconTrash } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';
import type { WidgetLayout } from '../types';
import {
  widgetDataSourceOf,
  widgetLabelFallback,
  widgetLabelKey,
  widgetSourceLabelFallback,
  widgetSourceLabelKey,
  type WidgetDataSource
} from '../widgetCatalog';
import { type WidgetType } from '../Widgets/types';
import {
  isCustomTint,
  isTintableWidgetType,
  WIDGET_TINT_CLASS,
  WIDGET_TINT_TOKENS,
  type WidgetTint
} from '../WidgetCard/tints';
import { getReadmodelCollection } from '../Widgets/readmodelSource';
import { getCubeOptions, getFieldsForCube, getMeasuresForCube } from '../Widgets/WidgetBuilder/cubeMeta';
import ReadmodelSourceSection, { validateReadmodelWidget } from './ReadmodelSourceSection';
import BasicSection from '../Widgets/WidgetBuilder/Sections/Basic/Basic';
import DateRangeSection from '../Widgets/WidgetBuilder/Sections/DateRange/DateRange';
import DimensionsSection from '../Widgets/WidgetBuilder/Sections/Dimensions/Dimensions';
import FiltersSection from '../Widgets/WidgetBuilder/Sections/Filters/Filters';
import MetricsSection from '../Widgets/WidgetBuilder/Sections/Metrics/Metrics';
import OrderBySection from '../Widgets/WidgetBuilder/Sections/OrderBy/OrderBy';
import WidgetPropsSection from '../Widgets/WidgetBuilder/Sections/WidgetProps/WidgetProps';
import type { FieldType, WidgetDefinition } from '../Widgets/WidgetBuilder/types';
import { validateWidget } from '../Widgets/validateWidget';
import classes from './WidgetInspector.module.css';

/** Which of the two panes is showing. Derived by the host from selection. */
export type WidgetInspectorPane = 'layout' | 'card';

/**
 * The card background tokens.
 *
 * ⚠️ RE-EXPORTED, NOT DECLARED. Ticket 13 moved the definition to
 * `WidgetCard/tints.ts`, where it belongs: the CARD is what has to paint a tint
 * for a READER, and this panel is one optional editor affordance on top of it.
 * Ticket 16 (this file) declared them first only because it landed first, and it
 * left behind a second value table in `WidgetInspector.module.css` plus a comment
 * asking whoever came next to keep the two in step by hand — exactly the drift
 * this re-export removes. The swatches now paint from `WIDGET_TINT_CLASS`, so
 * there is ONE token list and ONE value table.
 *
 * Kept exported from here because the panel's own tests and its hosts already
 * import them from this module; the names are identical objects, not copies.
 */
export {
  WIDGET_TINT_TOKENS,
  TINTABLE_WIDGET_TYPES,
  isTintableWidgetType,
  isCustomTint
} from '../WidgetCard/tints';
export type { WidgetTint } from '../WidgetCard/tints';

const TINT_LABEL: Record<WidgetTint, string> = {
  none: 'Sin fondo',
  teal: 'Verde azulado',
  amber: 'Ámbar',
  green: 'Verde',
  red: 'Rojo',
  navy: 'Azul',
  slate: 'Gris'
};

/**
 * Which sections the card pane mounts, PER DATA SOURCE (TV1-2291 · S26).
 *
 * The pane used to be Cube-shaped with one exception carved out for `text`
 * (`isDatalessWidgetType`). It is now driven by the catalog's `source`, because
 * the SDD's third source is a genuinely different query grammar rather than an
 * absence of one:
 *
 * | Source | Sections | Why |
 * |---|---|---|
 * | `analytics` | the seven verbatim `WidgetBuilder/Sections/*` | a Cube query |
 * | `readmodel` | collection / field / operator / value + props | a PV3 readmodel query — no cube, no measures, a DIFFERENT filter grammar |
 * | `static`    | basic + props (the content) | no query at all |
 *
 * ⚠️ Derived from `widgetDataSourceOf( type )` — the ticket-18 catalog — and never
 * from the type's SPELLING. That is not pedantry: `criticalStock` and `count` are
 * both "an operational-sounding number", `table` and `list` are both "rows", and
 * exactly one of each pair is Cube-backed. The catalog is where that fact is
 * written down once, next to the label and the description, so the palette's
 * grouping and the pane's field set cannot disagree.
 */
const ANALYTICS_SECTIONS = [ 'basic', 'metrics', 'dimensions', 'filters', 'orderBy', 'dateRange', 'widgetProps' ] as const;
const READMODEL_SECTIONS = [ 'readmodelSource', 'widgetProps' ] as const;
const STATIC_SECTIONS = [ 'basic', 'widgetProps' ] as const;

type InspectorSection = typeof ANALYTICS_SECTIONS[number] | typeof READMODEL_SECTIONS[number];

const SECTIONS_BY_SOURCE: Record<WidgetDataSource, readonly InspectorSection[]> = {
  analytics: ANALYTICS_SECTIONS,
  readmodel: READMODEL_SECTIONS,
  static: STATIC_SECTIONS
};

/**
 * Sections that cannot be configured before a CUBE is chosen.
 *
 * Analytics-only by construction: a readmodel card's equivalent gate (choose a
 * collection before its fields exist) lives inside `ReadmodelSourceSection`,
 * because collection and filter are ONE section there rather than six.
 */
const DATA_SOURCE_DEPENDENT: ReadonlySet<string> = new Set([ 'metrics', 'dimensions', 'filters', 'orderBy', 'dateRange' ]);

const SECTION_LABEL: Record<InspectorSection, { key: string; fallback: string }> = {
  basic: { key: 'widget_builder_sections_basic', fallback: 'Básico' },
  metrics: { key: 'widget_builder_sections_metrics', fallback: 'Métricas' },
  dimensions: { key: 'widget_builder_sections_dimensions', fallback: 'Dimensiones' },
  filters: { key: 'widget_builder_sections_filters', fallback: 'Filtros' },
  orderBy: { key: 'widget_builder_sections_order_by', fallback: 'Ordenar por' },
  dateRange: { key: 'widget_builder_sections_date_range', fallback: 'Rango de fechas' },
  widgetProps: { key: 'widget_builder_sections_widget_props', fallback: 'Propiedades' },
  readmodelSource: { key: 'widget_inspector_sections_readmodel', fallback: 'Origen de datos' }
};

const sourceLabelOf = (
  t: ( key: string, fallback: string ) => string,
  source: WidgetDataSource
): string => t( widgetSourceLabelKey( source ), widgetSourceLabelFallback( source ));

export type WidgetInspectorProps = {
  /** The visible pane. Controlled — the host derives it from selection. */
  pane: WidgetInspectorPane;
  /**
   * Dismiss the panel entirely.
   *
   * Replaces S16's `onPaneChange`, which existed to walk between three panes. With
   * the palette gone the panel is no longer a place you NAVIGATE — it is open on
   * one subject or it is not open at all, so the only control it needs is a way
   * out. The host clears the selection here, which is what actually closes it.
   */
  onClose: () => void;
  /** The report being edited; supplies the layout pane's settings and facts. */
  layout: WidgetLayout;
  /** Report-level edits (name, description). Same write path as a card move. */
  onLayoutChange: ( layout: WidgetLayout ) => void;
  /** The selected card's definition, or `null` when nothing is selected. */
  selectedWidget: WidgetDefinition | null;
  /** Emitted on EVERY edit — there is no save round-trip. */
  onWidgetChange: ( definition: WidgetDefinition ) => void;
  /**
   * The selected card's tint token.
   *
   * Deliberately a prop pair rather than a field on `selectedWidget`: `tint` rides
   * on the widget WRAPPER (beside x/y/width/height), not inside the definition —
   * see `UiWidgetsLayoutSchema` (ticket 20). The definition is the LLM output gate,
   * and any top-level field added to it is inherited by the model-facing schema;
   * a per-definition tint would hand a model a card-presentation key it must never
   * author. The host owns the wrapper, so the host owns this.
   */
  tint?: string;
  onTintChange: ( tint: WidgetTint ) => void;
  /**
   * The selected card's TEXT colour, if the author set one.
   *
   * Same wrapper, same reasoning as `tint` above — it rides beside x/y/width/height
   * and never inside the definition, because the definition is the model-facing
   * schema. Independent of `tint`: a card can have chosen paper, chosen ink, both
   * or neither.
   */
  textColor?: string;
  /** `undefined` clears it and hands the card back to the theme's default pair. */
  onTextColorChange: ( textColor: string | undefined ) => void;
  /** Remove the selected card. The host owns the confirmation modal. */
  onRemoveWidget: ( widgetId: string ) => void;
  /**
   * Widget types this device/report can hold.
   *
   * Still needed with the palette gone: `BasicSection` offers it as the selected
   * card's TYPE picker (turn this bar chart into a line chart), which is an edit to
   * an existing card rather than a way to add one.
   */
  availableWidgets: WidgetType[];
  /**
   * The live `/meta` catalog, OWNED BY THE EDITOR (2026-08-17).
   *
   * This panel used to call `useCubeMeta( cubeApi )` itself. It stopped being the
   * right owner for two reasons: the panel is now mounted on demand, so the catalog
   * would be re-fetched and discarded every time it opened and closed; and the
   * editor needs the same catalog anyway, to build an analytics preset against a
   * measure that actually exists. One fetch per editor session, passed down.
   */
  cubeCatalog: NormalizedCubeCatalog | null;
  cubeMetaLoading?: boolean;
  cubeMetaError?: string | null;
};

export default function WidgetInspector({
  pane,
  onClose,
  layout,
  onLayoutChange,
  selectedWidget,
  onWidgetChange,
  tint,
  onTintChange,
  textColor,
  onTextColorChange,
  onRemoveWidget,
  availableWidgets,
  cubeCatalog,
  cubeMetaLoading,
  cubeMetaError
}: WidgetInspectorProps ) {
  const { t, i18n } = useTranslation( 'widgets' );
  const raw = i18n.language?.split( '-' )[0];
  const language = ([ 'en', 'es', 'pt' ].includes( raw ) ? raw : 'es' ) as 'en' | 'es' | 'pt';
  const device = layout.device === 'mobile' ? 'mobile' : 'web';

  const [ openSection, setOpenSection ] = useState<string | null>( 'basic' );

  const availableCubeOptions = useMemo(() => getCubeOptions( cubeCatalog ), [cubeCatalog]);

  /*
   * `selectedWidget` is a prop, so two edits dispatched in the SAME tick would
   * both read the pre-edit value and the first would be lost — and the sections
   * do exactly that (choosing a cube resets measures, dimensions, filters,
   * orderBy and dateRange). The modal was immune because it held the config in
   * `useState` and updated it functionally.
   *
   * `latestRef` carries the most recently EMITTED definition so successive
   * updates in one tick compose, and it re-syncs after commit whenever the host
   * hands down a different object (a new selection, or a rejected edit). The
   * ref is only ever READ from event handlers, which run after that effect.
   */
  const latestRef = useRef<WidgetDefinition | null>( selectedWidget );
  useEffect(() => {
    latestRef.current = selectedWidget;
  }, [selectedWidget]);

  const applyUpdate = useCallback(
    ( update: ( current: WidgetDefinition ) => WidgetDefinition ) => {
      const base = latestRef.current;
      if ( !base ) return;
      const next = update( base );
      latestRef.current = next;
      onWidgetChange( next );
    },
    [onWidgetChange]
  );

  const updateConfig = useCallback(
    ( key: keyof WidgetDefinition, value: unknown ) => {
      applyUpdate(( current ) => ({ ...current, [key]: value }));
    },
    [applyUpdate]
  );

  const getAvailableFields = useCallback(
    (): FieldType[] => getFieldsForCube( cubeCatalog, selectedWidget?.cube ?? '' ),
    [ cubeCatalog, selectedWidget?.cube ]
  );

  const getAvailableMeasures = useCallback(
    (): { value: string; label: string }[] => getMeasuresForCube( cubeCatalog, selectedWidget?.cube ?? '' ),
    [ cubeCatalog, selectedWidget?.cube ]
  );

  /*
   * WHERE THE CARD'S SOURCE COMES FROM: the ticket-18 catalog, keyed by type —
   * never a `type.includes( 'Chart' )`-style read of the name (which is exactly
   * the classifier ticket 18 deleted, and which had shipped two permanently empty
   * palette tabs). `widgetDataSourceOf` takes a `string` and falls back to
   * `analytics`, so a persisted layout carrying a type this build does not know
   * still gets the Cube pane rather than an empty one.
   */
  const dataSource: WidgetDataSource = selectedWidget
    ? widgetDataSourceOf( selectedWidget.type )
    : 'analytics';

  /*
   * The modal validated on Save and jumped to the first failing section. There is
   * no Save here, so validation is continuous instead: the sections render their
   * own inline errors from this, and the accordion marks the failing ones.
   *
   * A readmodel card takes a different validator. `validateWidget` dispatches
   * through `widgetValidators`, an exhaustive `Record<WidgetType, …>` that
   * `count`/`alert`/`list`/`progress` are not members of yet (ticket 25 widens the
   * union together with their renderers), so it would answer
   * `Invalid widget type: alert` for every one of them — an error badge on a
   * section the pane does not even mount. `validateReadmodelWidget` checks the two
   * things the BINDING can get wrong instead, both schema-required.
   */
  const validationErrors = useMemo(
    () => {
      if ( !selectedWidget ) return null;
      return dataSource === 'readmodel'
        ? validateReadmodelWidget( selectedWidget, t )
        : validateWidget( selectedWidget, device, t );
    },
    [ selectedWidget, dataSource, device, t ]
  );

  const visibleSections: readonly InspectorSection[] = SECTIONS_BY_SOURCE[dataSource];

  /*
   * The open accordion item, CLAMPED to what this card actually shows. `basic` is
   * the default, and it is not a member of a readmodel card's section list — so
   * without the clamp, selecting a readmodel card would render an accordion with
   * every item collapsed and no way to tell why.
   *
   * Derived rather than synced through an effect on purpose: an effect would also
   * reset the user's chosen section on every re-selection of a same-source card,
   * and it would run a render LATE, so the first paint after a selection change
   * would still show the stale one.
   */
  const activeSection: InspectorSection | null =
    openSection && visibleSections.includes( openSection as InspectorSection )
      ? ( openSection as InspectorSection )
      : ( visibleSections[0] ?? null );

  /** The collection a readmodel card reads, for the header badge. */
  const readmodelCollection = selectedWidget?.collection
    ? getReadmodelCollection( selectedWidget.collection )
    : undefined;

  const widgetTypeLabel = ( type: WidgetType ): string =>
    t( widgetLabelKey( type ), widgetLabelFallback( type ));

  const renderSection = ( section: InspectorSection ) => {
    if ( !selectedWidget ) return null;

    const commonProps = {
      language,
      getAvailableFields,
      getAvailableMeasures,
      updateConfig,
      config: selectedWidget,
      validationErrors
    };

    switch ( section ) {
      case 'basic':
        return (
          <BasicSection
            {...commonProps}
            availableCubes={availableCubeOptions}
            cubeMetaLoading={cubeMetaLoading}
            cubeMetaError={cubeMetaError}
            selectedCube={selectedWidget.cube}
            setSelectedCube={( value ) => applyUpdate(( current ) => ({
              ...current,
              cube: value,
              // A new cube invalidates everything derived from the old one. One
              // patch, not five updateConfig calls — see `latestRef` above.
              measures: [],
              dimensions: [],
              filters: [],
              orderBy: [],
              dateRange: null
            }))}
            availableWidgetTypes={availableWidgets}
            widgetType={selectedWidget.type}
            setWidgetType={( value ) => applyUpdate(( current ) => ({
              ...current,
              type: value,
              widgetComponentProps: {}
            }))}
          />
        );
      case 'metrics':
        return <MetricsSection {...commonProps} />;
      case 'dimensions':
        return <DimensionsSection {...commonProps} />;
      case 'filters':
        return <FiltersSection {...commonProps} />;
      case 'orderBy':
        return <OrderBySection {...commonProps} />;
      case 'dateRange':
        return <DateRangeSection {...commonProps} />;
      case 'widgetProps':
        return <WidgetPropsSection {...commonProps} widgetType={selectedWidget.type} device={device} />;
      case 'readmodelSource':
        /*
         * `applyUpdate`, NOT `updateConfig`. Changing the collection has to rewrite
         * `collection` AND repoint every filter clause at a field the new
         * collection has; two `updateConfig` calls in one tick would both read the
         * pre-edit definition and the first would be lost — the same trap
         * `BasicSection`'s cube change avoids by emitting one functional patch.
         */
        return (
          <ReadmodelSourceSection
            definition={selectedWidget}
            applyUpdate={applyUpdate}
            validationErrors={validationErrors}
          />
        );
      default:
        return null;
    }
  };

  /**
   * The way out. Keeps `reportsInspectorBack`'s testid — it is the same control in
   * the same corner, and renaming it would churn every spec for a label change.
   */
  const closeButton = (
    <UnstyledButton
      className={classes.paneBack}
      data-testid={componentsTestIds.reportsInspectorBack}
      onClick={onClose}
    >
      <IconArrowLeft size={13} stroke={1.8} />
      {t( 'widget_inspector_close', 'Cerrar panel' )}
    </UnstyledButton>
  );

  // A `card` pane with nothing selected has nothing to show; fall back to the
  // report-level pane rather than rendering an empty shell.
  const activePane: WidgetInspectorPane = pane === 'card' && !selectedWidget ? 'layout' : pane;

  return (
    <aside className={classes.inspector} data-testid={componentsTestIds.reportsInspectorPanel}>
      {/*
        ⚠️ THE CLOSE CONTROL IS OUTSIDE THE SCROLLING AREA (2026-08-19).

        Ticket owner: *"the widget editor side panel [needs] the cerrar panel always
        visible, even when scrolling, because its difficult to close it otherwise."*

        It used to be the first child of each PANE, and the pane is what scrolls —
        so on a card with seven configuration sections open, the only way out
        scrolled off the top and you had to scroll back up to leave. The panel is now
        a fixed header plus a scrolling body, which is why `.inspector` stopped being
        the scroll container and `.inspectorBody` became it.

        A header rather than `position: sticky`: sticky inside a padded, gapped flex
        column needs its own opaque background and a z-index to stop the content
        showing through underneath it, and gets the padding wrong at the top of the
        scroll. Two boxes are simpler and cannot bleed.
      */}
      <div className={classes.inspectorHeader}>
        {closeButton}
      </div>

      <div className={classes.inspectorBody}>
      {activePane === 'layout' && (
        <div className={classes.pane} data-testid={componentsTestIds.reportsInspectorPane( 'layout' )}>
          <Text className={classes.paneHeading}>
            {t( 'widget_inspector_layout_heading', 'Informe' )}
          </Text>

          <div>
            <Text className={classes.fieldLabel}>
              {t( 'widget_inspector_layout_name_label', 'Nombre' )}
            </Text>
            <TextInput
              size="xs"
              value={layout.name ?? ''}
              placeholder={t( 'widget_inspector_layout_name_placeholder', 'Nombre del informe' )}
              onChange={( event ) => onLayoutChange({ ...layout, name: event.currentTarget.value })}
            />
          </div>

          <div>
            <Text className={classes.fieldLabel}>
              {t( 'widget_inspector_layout_description_label', 'Descripción' )}
            </Text>
            <Textarea
              size="xs"
              autosize
              minRows={2}
              value={layout.description ?? ''}
              placeholder={t( 'widget_inspector_layout_description_placeholder', 'Para qué sirve este informe' )}
              onChange={( event ) => onLayoutChange({ ...layout, description: event.currentTarget.value })}
            />
          </div>

          <div>
            <Text className={classes.fieldLabel}>
              {t( 'widget_inspector_layout_gap_label', 'Separación entre tarjetas' )}
            </Text>
            {/*
              The report's own gutter (2026-08-17). A SLIDER rather than a number
              box: the useful range is small and the choice is entirely visual, so
              dragging while watching the canvas is the actual workflow — and the
              canvas repaints live, because `gap` reaches the geometry the same way
              `cols` does.

              0–48 mirrors the domain schema's own bounds, so the control cannot
              author a value the save gate would reject. Committed through
              `onLayoutChange` like the name and description, so it rides the same
              debounce and cannot fire a command per pixel of drag.
            */}
            <Slider
              min={0}
              max={48}
              step={2}
              label={( value ) => `${ value }px`}
              data-testid={componentsTestIds.reportsInspectorGap}
              value={layout.gap ?? WIDGET_GAP_PX}
              onChange={( value ) => onLayoutChange({ ...layout, gap: value })}
            />
          </div>

          <div className={classes.facts}>
            <div className={classes.factRow}>
              <span>{t( 'widget_inspector_layout_device', 'Dispositivo' )}</span>
              <span className={classes.factValue}>
                {device === 'mobile'
                  ? t( 'widget_inspector_device_mobile', 'Móvil' )
                  : t( 'widget_inspector_device_web', 'Escritorio' )}
              </span>
            </div>
            <div className={classes.factRow}>
              <span>{t( 'widget_inspector_layout_columns', 'Columnas' )}</span>
              <span className={classes.factValue}>{layout.cols ?? '—'}</span>
            </div>
            <div className={classes.factRow}>
              <span>{t( 'widget_inspector_layout_cards', 'Tarjetas' )}</span>
              <span className={classes.factValue}>{layout.widgets?.length ?? 0}</span>
            </div>
          </div>

          {/*
            WHAT IS DELIBERATELY ABSENT HERE (2026-08-17):

            - an "Agregar tarjeta" button, which opened a second palette. Adding a
              card is the floating bar's job. Two entry points for one action is
              the redundancy this rework removes, and a button here is how it
              would come back.
            - a "Ninguna tarjeta seleccionada" empty state. The panel is no longer
              mounted when nothing is selected, so that message described a state
              that can no longer occur — it existed only to fill a column that was
              reserved whether or not it had anything to say.
          */}
          <Text className={classes.hint}>
            {t(
              'widget_inspector_layout_hint',
              'Elegí una tarjeta del informe para configurarla, o usá la barra de abajo para agregar una.'
            )}
          </Text>
        </div>
      )}

      {activePane === 'card' && selectedWidget && (
        <div className={classes.pane} data-testid={componentsTestIds.reportsInspectorPane( 'card' )}>

          <div className={classes.cardHeader}>
            <Text className={classes.paneHeading}>{widgetTypeLabel( selectedWidget.type )}</Text>
            <Group gap="xxs">
              <Badge size="sm" radius="sm" variant="light" color="primary">
                {sourceLabelOf( t, dataSource )}
              </Badge>
              {/*
                The binding, named in the card's own vocabulary: a Cube name for a
                Cube card, the collection's LABEL for a readmodel one (never the
                raw `items_publishing_publications_readmodel`).
              */}
              {dataSource === 'readmodel' ? (
                readmodelCollection ? (
                  <Badge size="sm" radius="sm" variant="light" color="gray">{readmodelCollection.label}</Badge>
                ) : null
              ) : selectedWidget.cube ? (
                <Badge size="sm" radius="sm" variant="light" color="gray">{selectedWidget.cube}</Badge>
              ) : null}
            </Group>
          </div>

          <div>
            <Text className={classes.fieldLabel}>
              {t( 'widget_inspector_card_title_label', 'Título' )}
            </Text>
            <TextInput
              size="xs"
              data-testid={componentsTestIds.reportsInspectorTitleInput}
              value={selectedWidget.title ?? ''}
              placeholder={t( 'widget_inspector_card_title_placeholder', 'Título visible en la tarjeta' )}
              onChange={( event ) => updateConfig( 'title', event.currentTarget.value )}
            />
          </div>

          <Accordion
            value={activeSection}
            onChange={setOpenSection}
            variant="default"
            chevronPosition="right"
            classNames={{
              item: classes.accordionItem,
              control: classes.accordionControl,
              content: classes.accordionContent
            }}
          >
            {visibleSections.map(( section ) => {
              const disabled = DATA_SOURCE_DEPENDENT.has( section ) && !selectedWidget.cube;
              const hasErrors = !!validationErrors?.[section];
              return (
                <Accordion.Item key={section} value={section}>
                  <Accordion.Control
                    disabled={disabled}
                    data-testid={componentsTestIds.reportsInspectorSection( section )}
                  >
                    <Group gap="xxs" wrap="nowrap">
                      <span>{t( SECTION_LABEL[section].key, SECTION_LABEL[section].fallback )}</span>
                      {hasErrors ? <Badge size="xs" radius="sm" color="red" variant="light">!</Badge> : null}
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    {/* Only the open section is mounted: seven live forms in a
                        348px column is both slow and unreadable. */}
                    {activeSection === section ? renderSection( section ) : null}
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>

          <div>
            <Text className={classes.fieldLabel}>
              {t( 'widget_inspector_tint_label', 'Fondo' )}
            </Text>
            {isTintableWidgetType( selectedWidget.type ) ? (
              <Stack gap="xxs">
                <div className={classes.swatches}>
                  {WIDGET_TINT_TOKENS.map(( token ) => {
                    const active = ( tint ?? 'none' ) === token;
                    return (
                      <UnstyledButton
                        key={token}
                        className={`${ classes.swatch } ${ WIDGET_TINT_CLASS[token] } ${ active ? classes.swatchSelected : '' }`}
                        data-testid={componentsTestIds.reportsInspectorTint( token )}
                        aria-pressed={active}
                        aria-label={t( `widget_inspector_tint_${ token }`, TINT_LABEL[token])}
                        onClick={() => onTintChange( token )}
                      />
                    );
                  })}
                </div>

                {/*
                  THE COLOUR PICKER (2026-08-17), beside the presets rather than
                  instead of them.

                  The seven tokens stay because they are the fast path — one click,
                  already contrast-checked in both themes, and consistent across a
                  report. The picker is for the case they cannot serve: matching a
                  brand colour, or grouping cards by a hue nobody anticipated.

                  `format="hexa"` and nothing else: the value is written into a CSS
                  custom property on a SHARED document, and `#rrggbb` / `#rrggbbaa`
                  are the only spellings the domain schema and `isCustomTint` accept.
                  An `rgba()` string from a picker set to another format would be
                  dropped by the save-path guard and the colour would vanish on
                  reload.

                  OPACITY IS THE AUTHOR'S (2026-08-18, ticket owner: "colours can be
                  full opacity or 0 opacity, its a user desition"). `hexa` is what
                  turns on Mantine's alpha slider, and the extra byte rides in the
                  same field — the card composites the result over the page and picks
                  its ink from THAT, so a half-strength colour stays readable without
                  anyone reasoning about it.
                */}
                <ColorInput
                  size="xs"
                  format="hexa"
                  withEyeDropper
                  data-testid={componentsTestIds.reportsInspectorTintCustom}
                  label={t( 'widget_inspector_tint_custom_label', 'Color propio' )}
                  placeholder={t( 'widget_inspector_tint_custom_placeholder', 'Elegí un color' )}
                  value={isCustomTint( tint ) ? tint : ''}
                  onChange={( value ) => {
                    // Emitted on every keystroke while someone types a hex, so an
                    // incomplete value ("#ab") must not be pushed — it would fail
                    // the guard, be dropped, and make the field feel broken.
                    if ( isCustomTint( value )) onTintChange( value as WidgetTint );
                  }}
                />
                <Text className={classes.hint}>
                  {t(
                    'widget_inspector_tint_hint',
                    'Elegí cualquier color y su opacidad. La tarjeta usa ese color tal cual y elige sola el tono de texto que mejor se lea, en tema claro y oscuro.'
                  )}
                </Text>
              </Stack>
            ) : (
              <Text className={classes.note}>
                {/*
                  * TV1-2291 (t25) — the copy was chart/table-specific, and stopped being
                  * true the moment the readmodel types landed: a tint is refused on
                  * `alert` because its SEVERITY is already a colour a seller reads as a
                  * judgement, and on `progress` because the bar's contrast against its
                  * track IS the reading. Generalised to the rule all of them share —
                  * "the colour is already carrying meaning" — rather than growing a
                  * fourth clause per type. See `WidgetCard/tints.ts` for the per-type
                  * reasoning.
                  */}
                {/* 2026-08-18 — `alert` came OFF this list (review item 5: alerts
                    follow the Dashboard's fixed tiles and their colours are
                    pickable), so the copy no longer cites severity. What remains is
                    `progress`, whose reading IS the bar against its track, and
                    `spacer`, which is invisible by construction. */}
                {t( 'widget_inspector_tint_unavailable', 'Esta tarjeta no acepta fondo: acá el color ya es el dato (el avance de la barra contra su riel), y un fondo propio competiría con eso.' )}
              </Text>
            )}
          </div>

          {/*
            THE TEXT COLOUR (2026-08-18) — offered on EVERY type, unlike the tint.

            The tint is refused on charts, tables, alerts and progress bars because a
            background there competes with a colour that is already carrying data.
            None of that applies to ink: a darker label on a table is an ordinary
            choice, and refusing it would be applying the tint's rule somewhere it
            was never argued for.

            The clear button is not decoration. Absent means "follow the theme" —
            two different colours, one per mode — and once a colour is set it applies
            in both. There has to be a way back to the first behaviour, and it cannot
            be "type the default", because typing the default gives you a card that
            is stuck on that colour in dark mode.
          */}
          <div>
            <Text className={classes.fieldLabel}>
              {t( 'widget_inspector_text_color_label', 'Color del texto' )}
            </Text>
            <Stack gap="xxs">
              <Group gap="xxs" wrap="nowrap" align="flex-end">
                <ColorInput
                  size="xs"
                  format="hexa"
                  withEyeDropper
                  style={{ flex: 1 }}
                  data-testid={componentsTestIds.reportsInspectorTextColor}
                  placeholder={t( 'widget_inspector_text_color_placeholder', 'Según el tema' )}
                  value={isCustomTint( textColor ) ? textColor : ''}
                  onChange={( value ) => {
                    // Emitted on every keystroke while someone types a hex, so an
                    // incomplete value ('#ab') must not be pushed — it would fail the
                    // guard, be dropped, and make the field feel broken. Same rule the
                    // tint picker follows.
                    if ( isCustomTint( value )) onTextColorChange( value );
                  }}
                />
                <ActionIcon
                  size="md"
                  variant="subtle"
                  color="gray"
                  disabled={!textColor}
                  data-testid={componentsTestIds.reportsInspectorTextColorClear}
                  aria-label={t( 'widget_inspector_text_color_clear', 'Volver al color del tema' )}
                  onClick={() => onTextColorChange( undefined )}
                >
                  <IconRestore size={16} />
                </ActionIcon>
              </Group>
              <Text className={classes.hint}>
                {t(
                  'widget_inspector_text_color_hint',
                  'Sin elegir, el texto cambia solo entre tema claro y oscuro. Si elegís un color, se usa ese en los dos.'
                )}
              </Text>
            </Stack>
          </div>

          <Button
            size="sm"
            radius="xs"
            variant="outline"
            color="red"
            styles={{ label: { fontSize: '12px' }}}
            leftSection={<IconTrash size={16} />}
            data-testid={componentsTestIds.reportsInspectorRemove}
            onClick={() => onRemoveWidget( selectedWidget.id )}
          >
            {t( 'widget_inspector_remove_card', 'Quitar tarjeta' )}
          </Button>
        </div>
      )}
      </div>
    </aside>
  );
}
